import clsx from 'clsx'
import { connect } from 'react-redux'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import Switch from 'LuxKit/Switch/Switch'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { useAppDispatch } from 'hooks/reduxHooks'
import { updateUserPreferenceDestination } from 'api/userTravelPreferences'
import { API_CALL_SUCCESS } from 'actions/actionConstants'
import { UPDATE_USER_PREFERENCES_DESTINATION } from 'actions/apiActionConstants'
import config from 'constants/config'
import ModalContext from 'contexts/ModalContext'
import useSaveAlertChannels from 'hooks/Alerts/useSaveAlertChannels'
import Clickable from 'components/Common/Clickable'
import { usePlaceById } from 'hooks/usePlace'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import WatchDestinationModal from './WatchDestinationModal'
import type { DestinationWatchPreferences } from './WatchDestinationModal'
import { showDestinationUnwatchedSnackbar, showDestinationWatchingSnackbar } from './DestinationWatchlistSnackbar'

interface MappedStateProps {
  savedDestinations?: Array<App.UserPreferenceDestination>
}

interface Props {
  placeId: string
  userId: string
  className?: string
}

function HotelSearchAddDestinationToggle(props: Props & MappedStateProps) {
  const { savedDestinations, userId, placeId, className } = props
  const intl = useIntl()
  // A destination can be saved as a travel preference without being watched — the travel
  // preferences flows save it with `isAlertSelected: false`. Only the alert flag says whether
  // this switch is on, and it is the same flag the saved alerts tab reads.
  const isDestinationWatched = useMemo(
    () => !!savedDestinations?.some(destination => destination.placeId === placeId && destination.isAlertSelected),
    [savedDestinations, placeId],
  )

  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const place = usePlaceById(placeId)
  const placeName = place?.name
  const { save } = useSaveAlertChannels(userId)

  const alertPreferencesErrorText = intl.formatMessage({
    id: 'tDtay7zOyjv2JTKzkNyo',
    defaultMessage: 'We couldn’t save your alert preferences. Please try again.',
    description: 'Error shown when saving delivery channels before creating a destination offer alert fails',
  })

  const watchDestination = useCallback(async(preferences?: DestinationWatchPreferences): Promise<boolean> => {
    try {
      // The watch itself is written first. There is no endpoint that writes the watch and the
      // preferences together, so ordering is the only lever: a failure here leaves the account's
      // consent exactly as it was, rather than changing channels for a watch that never existed.
      // Called directly rather than through the API_CALL thunk, which resolves whether or not
      // the write succeeded — the modal would close and claim success on a failed save.
      const destination = await updateUserPreferenceDestination(
        userId,
        { preferences: { destination: { placeId, isAlertSelected: true } } },
        config.BRAND,
      )
      dispatch({ type: API_CALL_SUCCESS, api: UPDATE_USER_PREFERENCES_DESTINATION, data: destination })
    } catch {
      showSnackbar(alertPreferencesErrorText, 'critical')
      return false
    }

    // Absent on the undo path, which re-adds the destination without reopening the modal, so
    // there are no new delivery choices to save. `save` reports its own failure.
    if (preferences && !await save(preferences)) return false

    showDestinationWatchingSnackbar(intl, placeName ?? '')
    return true
  }, [dispatch, intl, save, userId, placeId, placeName, alertPreferencesErrorText])

  const unwatchDestination = useCallback(async() => {
    try {
      // Clear the alert flag rather than delete the destination: the same record backs the
      // travel preferences the user set elsewhere, and turning this switch off is not a request
      // to forget the destination.
      const destination = await updateUserPreferenceDestination(
        userId,
        { preferences: { destination: { placeId, isAlertSelected: false } } },
        config.BRAND,
      )
      dispatch({ type: API_CALL_SUCCESS, api: UPDATE_USER_PREFERENCES_DESTINATION, data: destination })
      showDestinationUnwatchedSnackbar(intl, placeName ?? '', watchDestination)
    } catch {
      showSnackbar(alertPreferencesErrorText, 'critical')
    }
  }, [dispatch, userId, placeId, intl, placeName, watchDestination, alertPreferencesErrorText])

  const displayWatchModal = useCallback(() => {
    showModal(<WatchDestinationModal placeId={placeId} onStartWatching={watchDestination} />)
  }, [showModal, placeId, watchDestination])

  const handleToggle = useCallback(() => {
    if (isDestinationWatched) {
      unwatchDestination()
    } else {
      displayWatchModal()
    }
  }, [isDestinationWatched, unwatchDestination, displayWatchModal])

  const trackNewOffersText = intl.formatMessage({
    id: '7xqqH_vgQbFdsFU8Lyk-',
    defaultMessage: 'Track new offers',
    description: 'Label next to the toggle that turns on alerts for new offers in the destination being searched',
  })

  // The place lookup resolves after the first render, and the promise is worthless without the
  // destination in it, so the line is left out rather than shown with a blank where the name goes.
  const trackNewOffersDescription = placeName ?
      intl.formatMessage({
        id: 'nopFYGzNzHtAYAmFBD3Z',
        defaultMessage: 'We’ll send you our best {placeName} offers the moment they become available.',
        description: 'Sub-label under the track new offers toggle promising alerts for the best offers in the destination being searched as soon as they are published',
      }, { placeName }) :
    undefined

  return (
    <Group className={clsx(className)} direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
      <Group direction="vertical" gap={2}>
        <Group direction="horizontal" gap={4} verticalAlign="center" horizontalAlign="start">
          <BodyText variant="medium" weight="bold">{trackNewOffersText}</BodyText>
          <Clickable onClick={displayWatchModal}>
            <LineInfoCircleIcon size="S" />
          </Clickable>
        </Group>
        {trackNewOffersDescription && <BodyText variant="small" colour="neutral-two">{trackNewOffersDescription}</BodyText>}
      </Group>
      <Switch checked={isDestinationWatched} onClick={handleToggle} />
    </Group>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState) => {
  return {
    savedDestinations: appState.userTravelPreferences.destinations.data,
  }
})(HotelSearchAddDestinationToggle)
